import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "@/lang";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t, scrollTo } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden bg-bei-navy">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src="/assets/hero-custom.png"
          alt=""
          className="h-[110%] w-full object-cover object-center opacity-75"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-bei-navy/85 via-bei-navy/35 to-bei-navy/10" />
      <div className="absolute inset-0 bg-gradient-to-b from-bei-navy/50 via-transparent to-bei-navy/90" />
      <div className="grid-overlay absolute inset-0" />
      <div className="absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-bei-brand/20 blur-[140px]" />
      <div className="absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-bei-cyan/15 blur-[140px]" />

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-40 sm:px-6 lg:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="hero-status-badge"
          className="mb-10 inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md"
        >
          <span className="pulse-dot h-2 w-2 rounded-full bg-emerald-400" />
          <span className="font-mono text-xs font-medium uppercase tracking-[0.2em] text-slate-200">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="max-w-5xl font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          {t.hero.lines.map((line, i) => (
            <MaskedLine key={`${line}-${i}`} delay={0.35 + i * 0.12}>
              {i === t.hero.lines.length - 1 ? (
                <span className="text-bei-cyan">{line}</span>
              ) : (
                line
              )}
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("#contact")}
            className="group flex items-center gap-3 rounded-full bg-bei-brand px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-bei-azure hover:shadow-[0_12px_40px_rgba(0,82,204,0.45)]"
          >
            {t.hero.ctaPrimary}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("#services")}
            className="rounded-full border border-white/25 px-7 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-bei-cyan hover:text-bei-cyan"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/10 pt-8"
        >
          <div data-testid="hero-stat-response">
            <p className="font-display text-2xl font-bold text-white sm:text-3xl">{t.hero.stat1Value}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 sm:text-xs">
              {t.hero.stat1Label}
            </p>
          </div>
          <div data-testid="hero-stat-coverage">
            <p className="font-display text-2xl font-bold text-white sm:text-3xl">{t.hero.stat2Value}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 sm:text-xs">
              {t.hero.stat2Label}
            </p>
          </div>
          <div data-testid="hero-stat-team">
            <p className="font-display text-2xl font-bold text-white sm:text-3xl">{t.hero.stat3Value}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 sm:text-xs">
              {t.hero.stat3Label}
            </p>
          </div>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollTo("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-slate-400 transition-colors hover:text-bei-cyan"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
